import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const SITE_NAME = 'MagicStay Homes';

// Page title translations
const pageTitles = {
  pt: {
    // Public pages
    home: 'Casas de Temporada em Orlando',
    search: 'Buscar Imóveis',
    plans: 'Planos e Preços',
    howItWorks: 'Como Funciona',
    about: 'Sobre Nós',
    contact: 'Contato',
    faq: 'Perguntas Frequentes',
    terms: 'Termos de Uso',
    privacy: 'Política de Privacidade',
    
    // Auth pages
    login: 'Entrar',
    register: 'Cadastrar',
    forgotPassword: 'Recuperar Senha',
    resetPassword: 'Redefinir Senha',
    languageSelect: 'Selecionar Idioma',
    
    // User pages
    myAccount: 'Minha Conta',
    favorites: 'Favoritos',
    savedSearches: 'Buscas Salvas',
    compare: 'Comparar Imóveis',
    guestDashboard: 'Painel do Hóspede',
    
    // Host pages
    hostDashboard: 'Painel do Anfitrião',
    createProperty: 'Novo Anúncio',
    editProperty: 'Editar Anúncio',
    myProperties: 'Meus Imóveis',
    propertyPlanSelection: 'Escolher Plano',
    propertySubscription: 'Assinatura',
    
    // Subscription pages
    subscriptionSuccess: 'Assinatura Confirmada',
    subscriptionCancel: 'Assinatura Cancelada',
    
    // Admin pages
    adminDashboard: 'Painel Administrativo',
    adminDiagnostics: 'Diagnósticos do Sistema',
    adminEditProperty: 'Editar Imóvel (Admin)',
    
    // Other
    helpCenter: 'Central de Ajuda',
    notFound: 'Página Não Encontrada',
    unauthorized: 'Acesso Negado',
  },
  en: {
    // Public pages
    home: 'Vacation Rentals in Orlando',
    search: 'Search Properties',
    plans: 'Plans & Pricing',
    howItWorks: 'How It Works',
    about: 'About Us',
    contact: 'Contact',
    faq: 'FAQ',
    terms: 'Terms of Use',
    privacy: 'Privacy Policy',
    
    // Auth pages
    login: 'Login',
    register: 'Sign Up',
    forgotPassword: 'Forgot Password',
    resetPassword: 'Reset Password',
    languageSelect: 'Select Language',
    
    // User pages
    myAccount: 'My Account',
    favorites: 'Favorites',
    savedSearches: 'Saved Searches',
    compare: 'Compare Properties',
    guestDashboard: 'Guest Dashboard',
    
    // Host pages
    hostDashboard: 'Host Dashboard',
    createProperty: 'New Listing',
    editProperty: 'Edit Listing',
    myProperties: 'My Properties',
    propertyPlanSelection: 'Choose Plan',
    propertySubscription: 'Subscription',
    
    // Subscription pages
    subscriptionSuccess: 'Subscription Confirmed',
    subscriptionCancel: 'Subscription Cancelled',
    
    // Admin pages
    adminDashboard: 'Admin Dashboard',
    adminDiagnostics: 'System Diagnostics',
    adminEditProperty: 'Edit Property (Admin)',
    
    // Other
    helpCenter: 'Help Center',
    notFound: 'Page Not Found',
    unauthorized: 'Access Denied',
  }
};

/**
 * Hook to set page title with proper localization
 * @param {string} pageKey - Key from pageTitles object
 * @param {string} customTitle - Optional custom title (e.g., property title)
 * @param {string} suffix - Optional suffix (e.g., city name)
 */
export const usePageTitle = (pageKey, customTitle = null, suffix = null) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    const lang = language === 'pt' ? 'pt' : 'en';
    let title;
    
    if (customTitle) {
      // For property pages: "Property Title - City | MagicStay Homes"
      title = suffix 
        ? `${customTitle} – ${suffix} | ${SITE_NAME}`
        : `${customTitle} | ${SITE_NAME}`;
    } else if (pageKey === 'home') {
      // Home page: "MagicStay Homes - Vacation Rentals in Orlando"
      title = `${SITE_NAME} – ${pageTitles[lang][pageKey]}`;
    } else {
      // Other pages: "Page Title | MagicStay Homes"
      const pageTitle = pageTitles[lang][pageKey] || pageKey;
      title = `${pageTitle} | ${SITE_NAME}`;
    }
    
    document.title = title;
    
    // Also update Open Graph title meta tag
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
  }, [pageKey, customTitle, suffix, language]);
};

/**
 * Hook specifically for property detail pages
 * @param {object} property - Property object with title and city
 */
export const usePropertyTitle = (property) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    if (property && property.title) {
      const city = property.city || '';
      const title = city 
        ? `${property.title} – ${city} | ${SITE_NAME}`
        : `${property.title} | ${SITE_NAME}`;
      
      document.title = title;
      
      // Update Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', title);
      }
    }
  }, [property, language]);
};

/**
 * Hook for subscription page with property title
 * @param {string} baseKey - Base page key (e.g., 'propertySubscription')
 * @param {string} propertyTitle - Property title
 */
export const useSubscriptionTitle = (baseKey, propertyTitle) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    const lang = language === 'pt' ? 'pt' : 'en';
    const baseTitle = pageTitles[lang][baseKey] || baseKey;
    
    let title;
    if (propertyTitle) {
      title = `${baseTitle} – ${propertyTitle} | ${SITE_NAME}`;
    } else {
      title = `${baseTitle} | ${SITE_NAME}`;
    }
    
    document.title = title;
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
  }, [baseKey, propertyTitle, language]);
};

export default usePageTitle;
